import type { FC } from "react"
import { useForm } from "react-hook-form"

import type { ProductInventoriesSearchParams } from "../../schemas"

import { ProductInventoryInventoryStatusFilter } from "./ProductInventoryInventoryStatusFilter"
import { ProductInventoryProductClassFilter } from "./ProductInventoryProductClassFilter"
import { ProductInventoryProductManagerFilter } from "./ProductInventoryProductManagerFilter"
import { ProductInventoryProductStatusFilter } from "./ProductInventoryProductStatusFilter"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import type { ComputedProductStatus, InventoryStatus } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface ProductInventoryFilterDrawerProps {
  open: boolean
  searchParams: ProductInventoriesSearchParams
  productStatusOptions: Option<ComputedProductStatus>[]
  productInventoryStatusOptions: Option<InventoryStatus>[]
  productManagerOptions: Option<string>[]
  productClassOptions: Option<string>[]
  onToggle: () => void
  onChange: <K extends keyof ProductInventoriesSearchParams>(key: K, value: ProductInventoriesSearchParams[K]) => void
  onClear: () => void
}

export const ProductInventoryFilterDrawer: FC<ProductInventoryFilterDrawerProps> = ({
  open,
  searchParams,
  productStatusOptions,
  productInventoryStatusOptions,
  productManagerOptions,
  productClassOptions,
  onToggle,
  onChange,
  onClear,
}) => {
  const productStatuses = searchParams.productStatuses ?? []
  const inventoryStatuses = searchParams.inventoryStatuses ?? []
  const productManagerIds = searchParams.productManagerIds ?? []
  const productClassIds = searchParams.productClassIds ?? []

  const { control } = useForm({
    defaultValues: { productStatuses, inventoryStatuses, productManagerIds, productClassIds },
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='product-inventory.filter-drawer'>
      <div className='flex h-full flex-col gap-y-4'>
        <ProductInventoryProductStatusFilter
          control={control}
          values={productStatuses}
          options={productStatusOptions}
          onChange={(values) => onChange("productStatuses", values)}
        />

        <ProductInventoryInventoryStatusFilter
          control={control}
          values={inventoryStatuses}
          options={productInventoryStatusOptions}
          onChange={(values) => onChange("inventoryStatuses", values)}
        />

        <ProductInventoryProductManagerFilter
          control={control}
          values={productManagerIds}
          options={productManagerOptions}
          onChange={(values) => onChange("productManagerIds", values)}
        />

        <ProductInventoryProductClassFilter
          control={control}
          values={productClassIds}
          options={productClassOptions}
          onChange={(values) => onChange("productClassIds", values)}
        />
      </div>
    </SearchFilterDrawer>
  )
}
